// eslint-disable-next-line no-restricted-imports
import {
  Description,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  CloseButton as HeadlessCloseButton,
  Dialog as HeadlessDialog,
} from "@headlessui/react";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";

import { CloseButton } from "./CloseButton";

import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";

export type DialogProps = {
  open: boolean;
  onClose: () => void;
  /** The title is used for accessibility purposes, and is displayed at the top of the dialog. */
  title?: ReactNode;
  /** The description is used for accessibility purposes. */
  description?: ReactNode;
  isWarningDescription?: boolean;
  /** Use the `children` prop to render what you want in the body of the dialog. */
  children: ReactNode;
  className?: string;
  dialogPanelClassName?: string;
  /** When false, hides the close button and prevents closing via Escape or backdrop click. Defaults to true. */
  dismissible?: boolean;
};

export const Dialog: FC<DialogProps> = ({
  open,
  onClose,
  title,
  description,
  isWarningDescription,
  children,
  className,
  dialogPanelClassName = "w-[400px]",
  dismissible = true,
}) => {
  const closeAndTrack = () => {
    trackEvent("Close modal", BUTTON_EVENT_CATEGORY);
    onClose();
  };

  return (
    <HeadlessDialog
      className={twMerge("relative z-50", className)}
      open={open}
      onClose={dismissible ? closeAndTrack : () => {}}
    >
      <div className="fixed inset-0 z-50">
        <DialogBackdrop className="absolute inset-0 bg-black/50" />

        <div className="absolute inset-0 flex w-full items-center justify-center p-4 md:p-10">
          <div className="relative max-h-[calc(100vh-2rem)] overflow-y-auto rounded-[12px] border border-black-tint-90 bg-white p-6 shadow-modal supports-squircle:rounded-[24px] supports-squircle:corner-squircle">
            {dismissible && (
              <div className="absolute right-3 top-3">
                <HeadlessCloseButton as={CloseButton} onClick={closeAndTrack} />
              </div>
            )}
            <DialogPanel className={dialogPanelClassName}>
              {title && (
                <DialogTitle
                  className={twMerge(
                    "mb-4 text-base font-medium text-black-tint-10",
                    dismissible && "pr-[46px]",
                  )}
                >
                  {title}
                </DialogTitle>
              )}
              <div className="text-sm font-normal text-black-tint-20">
                {description && (
                  <Description
                    className={twMerge(
                      "text-sm font-normal text-black-tint-20",
                      isWarningDescription ? "border-l-2 border-red-tint-20 pl-4" : undefined,
                    )}
                  >
                    {description}
                  </Description>
                )}
                {children}
              </div>
            </DialogPanel>
          </div>
        </div>
      </div>
    </HeadlessDialog>
  );
};

// eslint-disable-next-line no-restricted-imports
export { Description } from "@headlessui/react";
